import { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Activity, Loader2, RefreshCw, Radar, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const WINDOWS = [{ v: 60, l: "1h" }, { v: 180, l: "3h" }, { v: 360, l: "6h" }, { v: 1440, l: "24h" }];

function Refusals({ rows }) {
  if (!rows?.length) return null;
  const copy = () => {
    navigator.clipboard.writeText(rows.map((r) => `${r.at} ${r.mailbox} ${r.op} HTTP ${r.status}${r.retry_after ? ` Retry-After ${r.retry_after}` : ""} request-id ${r.id || "?"}`).join("\n"))
      .then(() => toast.success("Copied — paste into the Microsoft ticket"));
  };
  return (
    <div className="min-w-0" data-testid="mailbox-traffic-refusals">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-xs font-semibold text-slate-600">Recent refusals — Microsoft request-ids for the support case</div>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={copy} data-testid="mailbox-traffic-copy-refusals"><Copy className="w-3 h-3 mr-1" />Copy all</Button>
      </div>
      <div className="max-h-40 overflow-auto rounded border border-slate-100">
        <table className="w-full text-[11px]"><tbody>
          {rows.map((r, i) => (
            <tr key={`${r.id}-${i}`} className="border-t border-slate-100 first:border-0">
              <td className="py-0.5 px-1 tabular-nums text-slate-500 whitespace-nowrap">{(r.at || "").slice(11, 19)}</td>
              <td className="py-0.5 px-1 truncate max-w-[160px]">{r.mailbox}</td>
              <td className="py-0.5 px-1 text-slate-500">{r.op}</td>
              <td className="py-0.5 px-1 text-red-600 font-medium">{r.status}{r.retry_after ? ` · RA ${r.retry_after}s` : ""}</td>
              <td className="py-0.5 px-1 font-mono text-slate-600 truncate max-w-[300px]" title={r.id}>{r.id || "—"}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
    </div>
  );
}

function Bars({ series }) {
  const max = Math.max(1, ...series.map((s) => s.calls));
  return (
    <div className="flex items-end gap-px h-24 w-full" data-testid="graph-traffic-bars" title="Microsoft Graph calls per minute — red = refused (429/503)">
      {series.map((s) => (
        <div key={s.minute} className="flex-1 min-w-0 flex flex-col justify-end" title={`${s.minute} — ${s.calls} calls, ${s.refused} refused`}>
          <div className="bg-red-500/80" style={{ height: `${(s.refused / max) * 96}px` }} />
          <div className="bg-sky-500/70" style={{ height: `${((s.calls - s.refused) / max) * 96}px` }} />
        </div>
      ))}
    </div>
  );
}

function Table({ title, rows, testId }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="text-xs font-semibold text-slate-600 mb-1">{title}</div>
      <table className="w-full text-xs table-fixed">
        <colgroup><col /><col className="w-12" /><col className="w-14" /><col className="w-16" /></colgroup>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-slate-100">
              <td className="py-1 pr-2 truncate" title={r.key}>{r.key}</td>
              <td className="py-1 text-right tabular-nums">{r.calls}</td>
              <td className={`py-1 text-right tabular-nums whitespace-nowrap ${r.refused ? "text-red-600 font-semibold" : "text-slate-400"}`}>{r.refused ? `${r.refused} ✕` : "–"}</td>
              <td className="py-1 text-right tabular-nums text-slate-400 whitespace-nowrap">{r.avg_ms}ms</td>
            </tr>
          ))}
          {!rows.length && <tr><td className="py-2 text-slate-400">nothing yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function MailboxTrafficCard() {
  const [minutes, setMinutes] = useState(60);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState(null);
  const [probing, setProbing] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { setData((await api.get("/admin/graph-traffic", { params: { minutes } })).data); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not load mailbox traffic"); }
    finally { setBusy(false); }
  }, [minutes]);
  useEffect(() => { load(); }, [load]);

  const runProbe = async () => {
    setProbing(true);
    try { setProbe((await api.post("/admin/graph-traffic/probe", null, { params: { rounds: 3 } })).data); }
    catch (err) { toast.error(err.response?.data?.detail || "Probe failed"); }
    finally { setProbing(false); }
  };

  const cooling = Object.entries(data?.cooling_down || {});
  return (
    <Card className="mb-4" data-testid="mailbox-traffic-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Activity className="w-4 h-4" /> Mailbox API traffic (Microsoft Graph)
          {data && (
            <Badge variant="outline" className={data.this_host_is_production ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"} data-testid="mailbox-traffic-host-badge">
              {data.this_host_is_production ? "production" : "preview host — not production traffic"}
            </Badge>
          )}
          {cooling.map(([mb, s]) => <Badge key={mb} className="bg-red-100 text-red-800 border-red-200" data-testid="mailbox-traffic-cooling-badge">Microsoft refusing {mb} — paused {s}s</Badge>)}
          <div className="ml-auto flex items-center gap-1">
            {WINDOWS.map((w) => (
              <Button key={w.v} size="sm" variant={minutes === w.v ? "default" : "ghost"} className="h-7 px-2" onClick={() => setMinutes(w.v)} data-testid={`mailbox-traffic-window-${w.l}`}>{w.l}</Button>
            ))}
            <Button size="sm" variant="ghost" className="h-7" onClick={load} disabled={busy} data-testid="mailbox-traffic-refresh">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Every call the portal makes to Microsoft for the mailboxes, counted per minute. Red = Microsoft refused it (429/503).
          {data && <> Last {minutes} min: <b data-testid="mailbox-traffic-total">{data.total_calls}</b> calls (~{data.per_minute_avg}/min), <b className={data.refused ? "text-red-600" : ""} data-testid="mailbox-traffic-refused">{data.refused}</b> refused.</>}
          {data?.cap && (
            <span className="block mt-1" data-testid="mailbox-traffic-in-flight">
              Shared cap: at most <b>{data.cap}</b> calls in flight per mailbox across every server (Microsoft allows 4).
              {Object.entries(data.in_flight || {}).length > 0 && <> Right now: {Object.entries(data.in_flight).map(([mb, n]) => `${mb} ${n}/${data.cap}`).join(" · ")}.</>}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {data && <Bars series={data.series} />}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Table title="By mailbox" rows={data.by_mailbox} testId="mailbox-traffic-by-mailbox" />
            <Table title="By screen / background job" rows={data.by_source} testId="mailbox-traffic-by-source" />
            <Table title="By operation" rows={data.by_op} testId="mailbox-traffic-by-op" />
          </div>
        )}
        {data && <Refusals rows={data.recent_refusals} />}
        <div className="border-t pt-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={runProbe} disabled={probing} data-testid="mailbox-traffic-probe-btn">
              {probing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Radar className="w-3.5 h-3.5 mr-1" />}Ask Microsoft now — is a mailbox being refused?
            </Button>
            <span className="text-xs text-slate-500">One tiny request at a time, nothing else running. A mailbox refused here while the others answer in ~0.2s is that mailbox's own Exchange budget being used elsewhere — not the portal.</span>
          </div>
          {probe && (
            <table className="w-full text-xs mt-2" data-testid="mailbox-traffic-probe-result">
              <tbody>
                {probe.summary.map((r) => (
                  <tr key={r.mailbox} className="border-t border-slate-100">
                    <td className="py-1 font-medium">{r.mailbox}</td>
                    <td className={`py-1 ${r.refused ? "text-red-600 font-semibold" : "text-emerald-700"}`}>{r.refused ? `${r.refused} of ${r.calls} refused` : `all ${r.calls} answered`}</td>
                    <td className="py-1 tabular-nums text-slate-500">{r.avg_ms}ms avg</td>
                    <td className="py-1 text-slate-500 truncate max-w-[360px]" title={r.last_error}>{r.last_error}{r.request_ids?.length ? ` · ids ${r.request_ids.join(", ")}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
